import { useEffect, useMemo, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useAuth0 } from "@auth0/auth0-react";
import { ShoppingCartProvider } from "config/shoppingCartContext";
import { UserProvider } from "config/userContext";
import { NotificationDataProvider } from "config/notificationDataContext";
import { getUser } from "config/api/user.api";
import Home from "views/Home";
import Search from "views/Search";
import Signup from "views/Signup";
import PageNotFound from "views/PageNotFound";
import FrequentQuestions from "views/FrequentQuestions";
import AboutUs from "views/AboutUs";
import ShoppingCart from "views/ShoppingCart";
import MyProfileView from "views/MyProfile/MyProfileView";
import ProductDetailView from "views/ProductDetailView";
import { getShoppingCartItems } from "config/api/shoppingCartItems.api";
import useLocalStorage from "components/hooks/useLocalStorage";
import ProtectedRoute from "components/ProtectedRoute";
import Header from "components/Header";
import Footer from "components/Footer";
import Notification from "components/common/Notification";
import { CartProduct } from "components/common/common.types";

const App = () => {
  const { isAuthenticated, user } = useAuth0();
  const [userData, setUserData] = useState(
    useLocalStorage("userData", {}).storedValue
  );
  const userValue = useMemo(() => ({ userData, setUserData }), [userData]);

  const { setValue } = useLocalStorage("userData", {});

  const [shoppingCartItems, setShoppingCartItems] = useState(
    [] as CartProduct[]
  );
  const shoppingCartValue = useMemo(
    () => ({ shoppingCartItems, setShoppingCartItems }),
    [shoppingCartItems]
  );

  const [notificationData, setNotificationData] = useState(null);
  const NotificationDataStatus = useMemo(
    () => ({
      notificationData,
      setNotificationData,
    }),
    // eslint-disable-next-line
    [notificationData]
  );

  const getItemsByUser = async (userId: number) => {
    await getShoppingCartItems(userId)
      .then((data) => {
        const items = data.map((item: any) => {
          if (item.price > 0) {
            return item;
          } else {
            return {
              ...item,
              price: 0,
            };
          }
        });
        setShoppingCartItems(items);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    if (isAuthenticated && user?.email) {
      getUser(user.email).then((data) => {
        setUserData(data);
        getItemsByUser(data.id);
        setValue(data);
      });
    }
    // eslint-disable-next-line
  }, [user, isAuthenticated]);

  const queryClient = new QueryClient();

  return (
    <QueryClientProvider client={queryClient}>
      <UserProvider value={userValue}>
        <Router>
          <ShoppingCartProvider value={shoppingCartValue}>
            <NotificationDataProvider value={NotificationDataStatus}>
              <Header />
              <Routes>
                <Route path="/signup" element={<Signup />}></Route>
                <Route path="/" element={<Home />}></Route>
                <Route element={<ProtectedRoute />}>
                  <Route path="/miperfil" element={<MyProfileView />}></Route>
                  <Route path="/buscar/:query" element={<Search />}></Route>
                  <Route
                    path="/producto/:productId"
                    element={<ProductDetailView />}
                  ></Route>
                  <Route
                    path="/carrito/:cartStep"
                    element={<ShoppingCart />}
                  ></Route>
                  <Route path="/ayuda" element={<FrequentQuestions />}></Route>
                  <Route path="/conocenos" element={<AboutUs />}></Route>
                  <Route path="/404" element={<PageNotFound />}></Route>
                  <Route path="*" element={<Navigate to="/404" />} />
                </Route>
              </Routes>
              <Footer />
              <Notification top={true} />
            </NotificationDataProvider>
          </ShoppingCartProvider>
        </Router>
      </UserProvider>
    </QueryClientProvider>
  );
};

export default App;
